import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Alert, LinearProgress, Chip,
  Paper, FormControl, InputLabel, Select, MenuItem, TextField,
  IconButton, Collapse, Divider, Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
  Gavel as OperationalIcon,
  Cloud as AzureGovIcon,
  Search as SearchIcon,
  FolderOpen as GroupIcon,
  CheckCircle as DoneIcon,
  PlayCircle as ActiveIcon,
  Schedule as ScheduleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { get } from '../../../services/apiService';

const PRIMARY = '#0f1f3d';
const MUTED   = '#5f6368';
const SURFACE = '#F8F9FA';

const PILLAR_META = {
  operationalCompliance: { label: 'Operational Compliance', icon: OperationalIcon, color: '#1565c0' },
  azureGovCompliance:    { label: 'Azure Gov Compliance',   icon: AzureGovIcon,    color: '#6a1b9a' },
};

const DONE_STATES   = ['Closed', 'Done', 'Completed'];
const ACTIVE_STATES = ['Active', 'In Progress'];
const CAT_PREFIX    = 'compliance-category-';
const UNTAGGED      = '__untagged__';

function getStateColor(s) {
  if (DONE_STATES.includes(s))   return 'success';
  if (ACTIVE_STATES.includes(s)) return 'primary';
  if (s === 'Removed')           return 'default';
  return 'warning';
}

function getStateIcon(s) {
  if (DONE_STATES.includes(s))   return <DoneIcon   sx={{ fontSize: 14 }} />;
  if (ACTIVE_STATES.includes(s)) return <ActiveIcon sx={{ fontSize: 14 }} />;
  return null;
}

function MiniDonut({ value = 0, size = 36 }) {
  const v = Math.min(Math.max(Number(value), 0), 100);
  const color = v === 100 ? '#2e7d32' : v >= 67 ? '#4caf50' : v >= 33 ? '#ff9800' : '#f44336';
  const r = (size / 2) - 3.5, stroke = 3, circ = 2 * Math.PI * r;
  const dash = (v / 100) * circ;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width={size} height={size} style={{ position: 'absolute', transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`${color}25`} strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <Typography sx={{ fontSize: size === 36 ? '0.6rem' : '0.55rem', fontWeight: 700, color, lineHeight: 1 }}>
        {Math.round(v)}%
      </Typography>
    </Box>
  );
}

const SpBadge = ({ value }) => value ? (
  <Tooltip title="Story Points">
    <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: 'rgba(0,0,0,0.06)', display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      <Typography variant="caption" fontWeight={700} color="#1a1a1a" lineHeight={1}>{value}</Typography>
      <Typography variant="caption" color={MUTED} lineHeight={1} sx={{ fontSize: '0.65rem' }}>pts</Typography>
    </Box>
  </Tooltip>
) : null;

const PRIORITY_META = {
  1: { label: 'P1', color: '#c62828', bg: '#ffebee' },
  2: { label: 'P2', color: '#e65100', bg: '#fff3e0' },
  3: { label: 'P3', color: '#f57f17', bg: '#fffde7' },
  4: { label: 'P4', color: '#546e7a', bg: '#eceff1' },
};

const PriorityBadge = ({ value }) => {
  const meta = PRIORITY_META[value];
  if (!meta) return null;
  return (
    <Tooltip title={`Priority ${value}`}>
      <Box sx={{
        px: 0.75, py: 0.15, borderRadius: '5px',
        bgcolor: meta.bg, display: 'inline-flex', alignItems: 'center', flexShrink: 0
      }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: meta.color, lineHeight: 1.4 }}>
          {meta.label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const ChildrenList = ({ children, pillarColor }) => {
  if (!children.length) return (
    <Box px={3} py={1.5}><Typography variant="body2" color={MUTED}>No backlog items found.</Typography></Box>
  );
  return (
    <Box sx={{ bgcolor: 'rgba(0,0,0,0.015)', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
      {children.map((child, i) => {
        const si = getStateIcon(child.state);
        return (
          <Box key={child.id || i} sx={{
            px: 3.5, py: 0.9,
            display: 'flex', alignItems: 'center', gap: 1.5,
            borderBottom: i < children.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none',
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: pillarColor, flexShrink: 0, opacity: 0.6 }} />
            {si && <Box sx={{ color: DONE_STATES.includes(child.state) ? '#2e7d32' : '#1565c0', display: 'flex', flexShrink: 0 }}>{si}</Box>}
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem', color: '#2d3748', lineHeight: 1.4 }}>
              {child.title}
            </Typography>
            {child.assignedTo && (
              <Typography variant="caption" color={MUTED} sx={{ flexShrink: 0, fontSize: '0.72rem' }}>
                {child.assignedTo}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const FeatureRow = ({ feature, pillarColor, subCatMap }) => {
  const [open, setOpen]       = useState(false);
  const [children, setChildren] = useState(null);
  const [loadingC, setLoadingC] = useState(false);

  const toggle = async () => {
    if (!open && children === null) {
      setLoadingC(true);
      try {
        const res = await get(`/api/compliance-h2-2026/features/${feature.id}/children`);
        setChildren(res.success ? res.data : []);
      } catch { setChildren([]); }
      finally { setLoadingC(false); }
    }
    setOpen(p => !p);
  };

  const initiative = useMemo(() => {
    for (const t of (feature.tags || [])) {
      if (t.toLowerCase().startsWith(CAT_PREFIX)) return subCatMap[t.slice(CAT_PREFIX.length)] || null;
    }
    return null;
  }, [feature.tags, subCatMap]);

  const adoUrl = `https://dev.azure.com/_workitems/edit/${feature.id}`;

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, py: 1.1,
        '&:hover': { bgcolor: 'rgba(0,0,0,0.025)' },
        cursor: 'pointer',
      }} onClick={toggle}>
        <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}>
          {open ? <ExpandMoreIcon sx={{ fontSize: 18, color: MUTED }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: MUTED }} />}
        </IconButton>
        <MiniDonut value={feature.completionRate} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1a1a1a', lineHeight: 1.35 }} noWrap>
            {feature.title}
          </Typography>
          {initiative && (
            <Chip label={initiative} size="small" sx={{ mt: 0.25, height: 16, fontSize: '0.62rem', bgcolor: `${pillarColor}14`, color: pillarColor, '& .MuiChip-label': { px: 0.75 } }} />
          )}
        </Box>
        <PriorityBadge value={feature.priority} />
        <Chip
          label={feature.state || 'New'}
          size="small"
          color={getStateColor(feature.state)}
          icon={getStateIcon(feature.state) || undefined}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.68rem', '& .MuiChip-label': { px: 0.75 } }}
        />
        <SpBadge value={feature.storyPoints} />
        {feature.backlogItemCount > 0 && (
          <Typography variant="caption" color={MUTED} sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
            {feature.backlogItemCount} item{feature.backlogItemCount !== 1 ? 's' : ''}
          </Typography>
        )}
        <Tooltip title="Open in ADO">
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); window.open(adoUrl, '_blank'); }}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <OpenInNewIcon sx={{ fontSize: 14, color: MUTED }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={open}>
        {loadingC
          ? <LinearProgress sx={{ mx: 3 }} />
          : children && <ChildrenList children={children} pillarColor={pillarColor} />
        }
      </Collapse>
    </Box>
  );
};

const InitiativeGroup = ({ name, features, pillarColor, subCatMap, allOpen }) => {
  const [open, setOpen] = useState(true);

  useEffect(() => { setOpen(allOpen); }, [allOpen]);

  const done   = features.filter(f => DONE_STATES.includes(f.state)).length;
  const totalSP = features.reduce((s, f) => s + (f.storyPoints || 0), 0);

  return (
    <Box sx={{ mb: 1, border: '1px solid rgba(0,0,0,0.07)', borderRadius: '10px', overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(p => !p)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1, cursor: 'pointer', bgcolor: `${pillarColor}08`,
          '&:hover': { bgcolor: `${pillarColor}12` },
        }}
      >
        <GroupIcon sx={{ fontSize: 16, color: pillarColor, flexShrink: 0 }} />
        <Typography variant="body2" fontWeight={600} color="#1a1a1a" sx={{ flex: 1, fontSize: '0.82rem' }}>
          {name === UNTAGGED ? 'Untagged' : name}
        </Typography>
        <Typography variant="caption" color={MUTED} sx={{ fontSize: '0.72rem' }}>
          {done}/{features.length} done
          {totalSP > 0 && ` · ${totalSP} pts`}
        </Typography>
        {open ? <ExpandMoreIcon sx={{ fontSize: 16, color: MUTED }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: MUTED }} />}
      </Box>
      <Collapse in={open}>
        {features.map(f => (
          <FeatureRow key={f.id} feature={f} pillarColor={pillarColor} subCatMap={subCatMap} />
        ))}
      </Collapse>
    </Box>
  );
};

const H22026ProductEngineering = ({ initialFilter }) => {
  const [features, setFeatures]       = useState([]);
  const [subCats, setSubCats]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [pillarFilter, setPillarFilter] = useState(initialFilter || 'all');
  const [search, setSearch]           = useState('');
  const [allOpen, setAllOpen]         = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [featRes, catRes] = await Promise.all([
          get('/api/compliance-h2-2026/features'),
          get('/api/compliance-h2-2026/subcategories'),
        ]);
        if (featRes.success) setFeatures(featRes.data || []);
        if (catRes.success)  setSubCats(catRes.data || []);
      } catch (e) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const subCatMap = useMemo(() => {
    const m = {};
    subCats.forEach(sc => { m[sc.alias] = sc.name; });
    return m;
  }, [subCats]);

  const filtered = useMemo(() => {
    let list = features;
    if (pillarFilter && pillarFilter !== 'all') list = list.filter(f => f.pillarKey === pillarFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(f => f.title?.toLowerCase().includes(q));
    }
    return list;
  }, [features, pillarFilter, search]);

  const grouped = useMemo(() => {
    const byPillar = {};
    for (const f of filtered) {
      if (!byPillar[f.pillarKey]) byPillar[f.pillarKey] = {};
      let initiative = UNTAGGED;
      for (const t of (f.tags || [])) {
        if (t.toLowerCase().startsWith(CAT_PREFIX)) {
          initiative = subCatMap[t.slice(CAT_PREFIX.length)] || t.slice(CAT_PREFIX.length);
          break;
        }
      }
      if (!byPillar[f.pillarKey][initiative]) byPillar[f.pillarKey][initiative] = [];
      byPillar[f.pillarKey][initiative].push(f);
    }
    return byPillar;
  }, [filtered, subCatMap]);

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box display="flex" gap={1.5} flexWrap="wrap" mb={2.5} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Pillar</InputLabel>
          <Select value={pillarFilter} label="Pillar" onChange={e => setPillarFilter(e.target.value)}>
            <MenuItem value="all">All Pillars</MenuItem>
            {Object.entries(PILLAR_META).map(([k, m]) => (
              <MenuItem key={k} value={k}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search features…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, color: MUTED, mr: 0.5 }} /> }}
          sx={{ minWidth: 220 }}
        />
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip title="Expand All">
            <IconButton size="small" onClick={() => setAllOpen(true)}><UnfoldMoreIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Collapse All">
            <IconButton size="small" onClick={() => setAllOpen(false)}><UnfoldLessIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {Object.entries(PILLAR_META).map(([pillarKey, meta]) => {
        const pillarData = grouped[pillarKey];
        if (!pillarData && (pillarFilter !== 'all' && pillarFilter !== pillarKey)) return null;
        if (!pillarData) return null;
        const Icon = meta.icon;

        return (
          <Paper key={pillarKey} elevation={0} sx={{ mb: 3, border: '1.5px solid rgba(0,0,0,0.07)', borderRadius: '14px', overflow: 'hidden' }}>
            <Box sx={{
              px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
              bgcolor: `${meta.color}0d`, borderBottom: '1px solid rgba(0,0,0,0.07)',
            }}>
              <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: `${meta.color}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon sx={{ fontSize: 18, color: meta.color }} />
              </Box>
              <Typography fontWeight={650} color="#1a1a1a" sx={{ fontSize: '0.9rem' }}>{meta.label}</Typography>
              <Typography variant="caption" color={MUTED} sx={{ ml: 0.5 }}>
                {Object.values(pillarData).flat().length} feature{Object.values(pillarData).flat().length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box sx={{ p: 1.5 }}>
              {Object.entries(pillarData).map(([initiative, feats]) => (
                <InitiativeGroup
                  key={initiative}
                  name={initiative}
                  features={feats}
                  pillarColor={meta.color}
                  subCatMap={subCatMap}
                  allOpen={allOpen}
                />
              ))}
            </Box>
          </Paper>
        );
      })}

      {filtered.length === 0 && (
        <Box textAlign="center" py={6}>
          <Typography color={MUTED}>No features found.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default H22026ProductEngineering;
